import { useState } from "react";
import { TextField, MenuItem } from "@mui/material";
import { useSnackbar } from "notistack";
import QuickCreateModal from "../common/QuickCreateModal";
import { useCreateLocationMutation } from "../../features/masterdata/masterdataApi";
import type { Location, LocationCreate, LocationType } from "../../features/masterdata/types";

const LOCATION_TYPES: { value: LocationType; label: string }[] = [
  { value: "hotel", label: "Hotel" },
  { value: "office", label: "Office" },
  { value: "stop", label: "Stop" },
  { value: "airport", label: "Airport" },
  { value: "port", label: "Port" },
  { value: "other", label: "Other" },
];

export interface QuickCreateLocationProps {
  open: boolean;
  onClose: () => void;
  onSuccess: (location: Location) => void;
}

export default function QuickCreateLocation({ open, onClose, onSuccess }: QuickCreateLocationProps) {
  const { enqueueSnackbar } = useSnackbar();
  const [createLocation, { isLoading }] = useCreateLocationMutation();

  const [name, setName] = useState("");
  const [type, setType] = useState<LocationType>("hotel");

  const handleSubmit = async (): Promise<Location> => {
    const payload: LocationCreate = { name: name.trim(), type };
    const created = await createLocation(payload).unwrap();
    enqueueSnackbar("Location created", { variant: "success" });
    return created;
  };

  const handleSuccess = () => {
    // Reset for next open
    setName("");
    setType("hotel");
  };

  return (
    <QuickCreateModal<Location>
      open={open}
      onClose={onClose}
      title="New location"
      onSubmit={handleSubmit}
      onSuccess={(loc) => {
        handleSuccess();
        onSuccess(loc);
      }}
      submitDisabled={!name.trim()}
      loading={isLoading}
      submitLabel="Save & select"
    >
      <TextField
        label="Name"
        value={name}
        onChange={(e) => setName(e.target.value)}
        required
        fullWidth
        autoFocus
        placeholder="e.g. Airport Main"
      />
      <TextField
        label="Type"
        select
        value={type}
        onChange={(e) => setType(e.target.value as LocationType)}
        fullWidth
      >
        {LOCATION_TYPES.map((t) => (
          <MenuItem key={t.value} value={t.value}>
            {t.label}
          </MenuItem>
        ))}
      </TextField>
    </QuickCreateModal>
  );
}
